import React, { useState, useMemo } from "react";
import { Users, UserPlus, Search, Filter, ShieldCheck, MoreVertical, Edit2, Ban, Trash2, Mail, ExternalLink, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import StatCard from "../../components/common/StatCard";

const GlassCard = ({ children, className = "" }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

// --- Dummy Data ---
const DUMMY_USERS = [
  { id: "USR-001", name: "David Osiota", email: "david@codehub.com", role: "SuperAdmin", status: "Active", joined: "Jan 12, 2023", avatar: "https://i.pravatar.cc/150?u=david" },
  { id: "USR-002", name: "Sarah Jenkins", email: "sarah.j@gmail.com", role: "Instructor", status: "Active", joined: "Mar 05, 2023", avatar: "https://i.pravatar.cc/150?u=sarah" },
  { id: "USR-003", name: "Michael Chen", email: "mchen@yahoo.com", role: "Student", status: "Active", joined: "Oct 18, 2023", avatar: "https://i.pravatar.cc/150?u=michael" },
  { id: "USR-004", name: "Emily Rodriguez", email: "emily.r@outlook.com", role: "Student", status: "Suspended", joined: "Sep 22, 2023", avatar: "https://i.pravatar.cc/150?u=emily" },
  { id: "USR-005", name: "James Wilson", email: "james.wilson@codehub.com", role: "Admin", status: "Active", joined: "Feb 28, 2023", avatar: "https://i.pravatar.cc/150?u=james" },
  { id: "USR-006", name: "Linda Ng", email: "linda.ng@gmail.com", role: "Instructor", status: "Active", joined: "Apr 15, 2023", avatar: "https://i.pravatar.cc/150?u=linda" },
  { id: "USR-007", name: "Robert Taylor", email: "robert.t@mail.com", role: "Student", status: "Active", joined: "Oct 25, 2023", avatar: "https://i.pravatar.cc/150?u=robert" },
];

const AdminUsers = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("All Roles");
  const [activeDropdown, setActiveDropdown] = useState(null);

  const filteredUsers = useMemo(() => {
    return DUMMY_USERS.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            user.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = filterRole === "All Roles" || user.role === filterRole;
      return matchesSearch && matchesRole;
    });
  }, [searchQuery, filterRole]);

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">User Management</h1>
          <p className="cht-text-gray-500 cht-font-medium">Global directory and access control for all platform members.</p>
        </div>
        <Button variant="primary" leftIcon={<UserPlus size={18} />} className="cht-rounded-2xl">
          Invite User
        </Button>
      </header>

      {/* KPI Grid */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-6">
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-blue-50 cht-text-blue-600 cht-rounded-2xl"><Users size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Total Users</p><h4 className="cht-text-2xl cht-font-black">12,840</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl"><ShieldCheck size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Instructors</p><h4 className="cht-text-2xl cht-font-black">148</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl"><Users size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Active Students</p><h4 className="cht-text-2xl cht-font-black">10,520</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-rose-50 cht-text-rose-600 cht-rounded-2xl"><Ban size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Suspended</p><h4 className="cht-text-2xl cht-font-black">32</h4></div>
        </GlassCard>
      </div>

      {/* Filter Bar */}
      <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-50 cht-shadow-sm">
        <div className="cht-relative cht-flex-1 cht-w-full">
          <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Search by name, email, or ID..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3.5 cht-pl-12 cht-pr-4 cht-text-sm cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
          />
        </div>
        <div className="cht-flex cht-items-center cht-gap-2 cht-w-full md:cht-w-auto">
          {["All Roles", "Student", "Instructor", "Admin", "SuperAdmin"].map(role => (
            <button
              key={role}
              onClick={() => setFilterRole(role)}
              className={`cht-flex-1 md:cht-flex-none cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${
                filterRole === role ? "cht-bg-[#1e293b] cht-text-white" : "cht-bg-gray-50 cht-text-gray-400 hover:cht-bg-gray-100"
              }`}
            >
              {role}
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <GlassCard className="cht-rounded-[40px] cht-overflow-visible">
        <div className="cht-overflow-x-auto">
          <table className="cht-w-full">
            <thead>
              <tr className="cht-border-b cht-border-gray-50">
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">User Details</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Role</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Status</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Joined</th>
                <th className="cht-px-8 cht-py-6 cht-text-right cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-50">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:cht-bg-gray-50/50 cht-transition-colors group">
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-flex cht-items-center cht-gap-4">
                      <div className="cht-w-12 cht-h-12 cht-rounded-[14px] cht-overflow-hidden cht-border cht-border-gray-100">
                        <img src={user.avatar} alt={user.name} className="cht-w-full cht-h-full cht-object-cover" />
                      </div>
                      <div className="cht-space-y-1">
                        <h4 className="cht-text-sm cht-font-black cht-text-gray-900 flex cht-items-center cht-gap-2">
                          {user.name} 
                          {user.role === 'SuperAdmin' && <ShieldCheck size={14} className="cht-text-blue-500" />}
                        </h4>
                        <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-text-gray-400 cht-font-medium">
                          <Mail size={12} /> {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <span className={`cht-px-3 cht-py-1.5 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest ${
                      user.role === 'SuperAdmin' ? 'cht-bg-blue-100 cht-text-blue-700' :
                      user.role === 'Admin' ? 'cht-bg-purple-100 cht-text-purple-700' :
                      user.role === 'Instructor' ? 'cht-bg-amber-100 cht-text-amber-700' :
                      'cht-bg-gray-100 cht-text-gray-600'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-flex cht-items-center cht-gap-2">
                      <div className={`cht-w-2 cht-h-2 cht-rounded-full ${user.status === 'Active' ? 'cht-bg-emerald-500' : 'cht-bg-rose-500'}`} />
                      <span className="cht-text-xs cht-font-bold cht-text-gray-700">{user.status}</span>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <p className="cht-text-xs cht-font-medium cht-text-gray-500">{user.joined}</p>
                  </td>
                  <td className="cht-px-8 cht-py-6 cht-text-right">
                    <div className="cht-relative cht-inline-block">
                      <button 
                        onClick={() => setActiveDropdown(activeDropdown === user.id ? null : user.id)}
                        className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-gray-400 hover:cht-bg-gray-100 hover:cht-text-gray-900 transition-colors"
                      >
                        <MoreVertical size={20} />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === user.id && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            className="cht-absolute cht-right-0 cht-mt-2 cht-w-48 cht-bg-white cht-rounded-2xl cht-shadow-[0_20px_60px_rgba(0,0,0,0.1)] cht-border cht-border-gray-50 cht-p-2 cht-z-[100]"
                          >
                            <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-rounded-xl transition-colors">
                              <ExternalLink size={16} /> View Profile
                            </button>
                            <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-rounded-xl transition-colors">
                              <Edit2 size={16} /> Edit Role
                            </button>
                            <div className="cht-h-px cht-bg-gray-100 cht-my-1" />
                            {user.status === 'Active' ? (
                              <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-amber-600 hover:cht-bg-amber-50 cht-rounded-xl transition-colors">
                                <Ban size={16} /> Suspend User
                              </button>
                            ) : (
                              <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-emerald-600 hover:cht-bg-emerald-50 cht-rounded-xl transition-colors">
                                <ShieldCheck size={16} /> Reactivate
                              </button>
                            )}
                            <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-xl transition-colors">
                              <Trash2 size={16} /> Delete Account
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="cht-py-20 cht-text-center">
              <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4 cht-text-gray-300">
                <Search size={32} />
              </div>
              <h3 className="cht-text-lg cht-font-black cht-text-gray-900">No users found</h3>
              <p className="cht-text-sm cht-text-gray-400 cht-font-medium">Try adjusting your filters or search terms.</p>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Invisible overlay to close dropdowns */}
      {activeDropdown && (
        <div 
          className="cht-fixed cht-inset-0 cht-z-[50]" 
          onClick={() => setActiveDropdown(null)} 
        />
      )}
    </div>
  );
};

export default AdminUsers;
